/**
 * useLibrary.ts — React binding for a LibraryAdapter.
 *
 * Owns the query string + the debounced search that feeds it, plus a
 * `revision` counter that any mutation bumps to trigger a re-fetch. Callers
 * get back `{ entries, loading, error, query, setQuery, refresh, run }` —
 * the same surface whether the adapter is local or remote.
 *
 * The hook does NOT know about scenarios/bundles — it only knows the
 * LibraryAdapter interface. UI components call `adapter.get(id)` when they
 * need the graph.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { LibraryAdapter, LibraryEntry, LibraryError } from './LibraryAdapter';

const DEBOUNCE_MS = 150;

export interface UseLibraryResult {
  entries: LibraryEntry[];
  loading: boolean;
  error: LibraryError | null;
  query: string;
  setQuery: (q: string) => void;
  /** Re-run the current search (e.g. after an external change). */
  refresh: () => void;
}

export function useLibrary(adapter: LibraryAdapter, initialQuery = ''): UseLibraryResult {
  const [query, setQuery] = useState(initialQuery);
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LibraryError | null>(null);
  const [revision, setRevision] = useState(0);
  const reqId = useRef(0);
  const refresh = useCallback(() => setRevision((r) => r + 1), []);

  useEffect(() => {
    let cancelled = false;
    const myReq = ++reqId.current;
    const timer = setTimeout(() => {
      setLoading(true);
      adapter
        .search(query)
        .then((res) => {
          if (cancelled || reqId.current !== myReq) return;
          setEntries(res);
          setError(null);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (cancelled || reqId.current !== myReq) return;
          setError(e instanceof LibraryError ? e : new LibraryError('network', String(e)));
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [adapter, query, revision]);

  return { entries, loading, error, query, setQuery, refresh };
}
